import { usePromotions } from "../context/PromotionContext";
import { parseDescriptionBlocks } from "../utils/productDescription";
import React, { useEffect, useState, useContext, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import { BsBasket } from "react-icons/bs";
import {
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiMaximize2,
  FiShield,
  FiShoppingCart,
} from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { MdPayment } from "react-icons/md";
import { RiDiscountPercentFill } from "react-icons/ri";
import { PiScales } from "react-icons/pi";
import { CartContext } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useComparison } from "../context/ComparisonContext";
import { useDeliverySettings } from "../context/DeliverySettingsContext";
import { formatUah } from "../api/siteSettingsApi";
import ReviewForm from "../components/ReviewForm";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductRatingSummary from "../components/ProductRatingSummary";
import ProductImageLightbox from "../components/ProductImageLightbox";
import styles from "./ProductDetail.module.css";
import productsStyles from "./Products.module.css";
import {
  PROMOTIONS_PATH,
  getCategoryByQuery,
  getCategoryBySlug,
} from "../config/navigation";
import {
  SITE_LOCALE,
  SITE_NAME,
  absoluteUrl,
  buildProductSchema,
  formatTitle,
} from "../config/seo";
import novaPoshtaLogo from "../assets/icons/nova-poshta.svg";
import {
  buildBrandPath,
  buildProductDetailPath,
  getProductVariantId,
} from "../utils/productLinks";
import {
  getProductAvailableQuantity,
  sortProductsByAvailability,
} from "../utils/productAvailability";
import {
  getProductGalleryImages,
  getProductImage,
} from "../utils/productImages";
import { pickRelatedProducts } from "../utils/productRecommendations";
import { getProductDisplayName } from "../utils/productDisplayName";
import { normalizeProductCategories } from "../utils/productCategories";
import {
  buildCategoryKindPath,
  buildPromotionCategoryPath,
  buildPromotionCategoryKindPath,
  findKindBySlug,
} from "../utils/categoryKindRoutes";
import {
  formatPrice,
  getActualProductPrice,
  getProductDiscountPercent as getCardDiscountPercent,
  getProductDiscountPrice,
  getProductSaving,
  getProductRegularPrice,
  safeNumber,
} from "../utils/productSorting";

const DEFAULT_IMAGE = "/images/default-product.jpg";
const NOVA_POSHTA_LOGO = novaPoshtaLogo;

const lowercaseFirst = (value = "") =>
  value ? value.charAt(0).toLocaleLowerCase("uk-UA") + value.slice(1) : "";

const getCategoryTargetPhrase = (categoryItem) =>
  lowercaseFirst(categoryItem?.query || "");

const formatCategoryBreadcrumbLabel = (categoryItem, fallback) =>
  categoryItem?.query || fallback || categoryItem?.label || categoryItem?.menuLabel || "";

const formatKindBreadcrumbLabel = (kind, categoryItem) => {
  const kindLabel = String(kind || "").trim();
  const targetPhrase = getCategoryTargetPhrase(categoryItem);

  if (!kindLabel) return "";
  if (!targetPhrase) return kindLabel;

  return kindLabel.toLocaleLowerCase("uk-UA").includes(targetPhrase)
    ? kindLabel
    : `${kindLabel} ${targetPhrase}`;
};
const STAR_SYMBOLS = "★★★★★";

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.products)) return data.products;
  return [];
};

const pluralizeReviews = (count) => {
  const value = Math.abs(Math.trunc(safeNumber(count)));
  const lastTwo = value % 100;
  const last = value % 10;

  if (lastTwo >= 11 && lastTwo <= 14) return "відгуків";
  if (last === 1) return "відгук";
  if (last >= 2 && last <= 4) return "відгуки";
  return "відгуків";
};

const arrayify = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const getPrimaryCategoryValue = (product = {}) =>
  product.primaryCategory ||
  product.mainCategory ||
  product.defaultCategory ||
  arrayify(product.category)[0] ||
  null;

const getProductCategoryItems = (product = {}) => {
  const seen = new Set();

  return arrayify(product.category).reduce((items, category) => {
    const item = getCategoryByQuery(category);
    if (!item || seen.has(item.slug)) return items;

    seen.add(item.slug);
    items.push(item);
    return items;
  }, []);
};

const productHasCategoryItem = (product = {}, categoryItem) => {
  if (!categoryItem?.query) return false;

  return arrayify(product.category).includes(categoryItem.query);
};

const formatDetailValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => {
        const trimmed = String(item).trim();
        const [first, ...rest] = Array.from(trimmed);
        return first
          ? `${first.toLocaleUpperCase("uk-UA")}${rest.join("")}`
          : "";
      })
      .join(", ");
  }

  if (typeof value === "boolean") return value ? "Так" : "Ні";
  return value || "";
};

const getYesNoDetailValue = (value) => {
  if (typeof value === "boolean") return value ? "Так" : "Ні";

  const normalizedValue = String(value ?? "")
    .trim()
    .toLocaleLowerCase("uk-UA");

  if (normalizedValue === "так") return "Так";
  if (normalizedValue === "ні") return "Ні";
  return "";
};

const buildImagePairs = (product, variant) => {
  const pairs = [
    ...getProductGalleryImages(variant, ""),
    ...getProductGalleryImages(product, ""),
  ].filter((image) => image?.original || image?.full || image?.thumbnail);

  const seen = new Set();
  const uniquePairs = pairs.filter((image) => {
    const key = image.original || image.full || image.thumbnail;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return uniquePairs.length
    ? uniquePairs
    : [{ thumbnail: DEFAULT_IMAGE, full: DEFAULT_IMAGE }];
};

const buildImageList = (product, variant) => {
  return buildImagePairs(product, variant).map(
    (image) => image.full || image.thumbnail || DEFAULT_IMAGE,
  );
};

const calculatePricePerKgOrLitre = (product) => {
  if (!product?.weight) return null;

  const price = safeNumber(product.discountPrice || product.price);
  const rawWeight = String(product.weight)
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(",", ".");

  let numericValue = null;
  let unit = "";

  if (/^\d+(\.\d+)?кг$/.test(rawWeight)) {
    numericValue = parseFloat(rawWeight.replace("кг", ""));
    unit = "кг";
  } else if (/^\d+(\.\d+)?г$/.test(rawWeight)) {
    numericValue = parseFloat(rawWeight.replace("г", "")) / 1000;
    unit = "кг";
  } else if (/^\d+(\.\d+)?л$/.test(rawWeight)) {
    numericValue = parseFloat(rawWeight.replace("л", ""));
    unit = "л";
  } else if (/^\d+(\.\d+)?мл$/.test(rawWeight)) {
    numericValue = parseFloat(rawWeight.replace("мл", "")) / 1000;
    unit = "л";
  } else {
    return null;
  }

  if (!numericValue || Number.isNaN(numericValue)) return null;

  const unitPrice = price / numericValue;
  if (!Number.isFinite(unitPrice) || unitPrice > 10000) return null;

  return {
    value: unitPrice.toFixed(2),
    label: unit === "л" ? "Ціна за 1 л" : "Ціна за 1 кг",
  };
};

const HIDDEN_DESCRIPTION_BLOCK_TITLES = new Set([
  "загальні характеристики",
  "загальний опис",
  "інше",
]);

const shouldRenderDescriptionBlockTitle = (title) =>
  Boolean(title) &&
  !HIDDEN_DESCRIPTION_BLOCK_TITLES.has(String(title).trim().toLowerCase());

const VARIANT_SORT_TYPE_ORDER = {
  measured: 0,
  numeric: 1,
  text: 2,
};
const VARIANT_DIMENSION_ORDER = {
  mass: 0,
  volume: 1,
};
const VARIANT_NUMBER_PATTERN = "\\d+(?:[.,]\\d+)?";
const VARIANT_UNIT_PATTERN =
  "(кілограма|кілограми|кілограмів|кілограм|кг|kg|міліграма|міліграми|міліграмів|міліграм|мг|mg|грама|грами|грамів|грам|гр|г|g|мілілітра|мілілітри|мілілітрів|мілілітр|мл|ml|літра|літри|літрів|літр|л|l)";
const VARIANT_PACK_MEASURE_PATTERN = new RegExp(
  `(${VARIANT_NUMBER_PATTERN})\\s*(?:шт\\.?|штук|pcs|pieces|pack|packs|уп\\.?|упаковки?)?\\s*(?:x|х|×|\\*|по)\\s*(${VARIANT_NUMBER_PATTERN})\\s*${VARIANT_UNIT_PATTERN}`,
  "i",
);
const VARIANT_REVERSE_PACK_MEASURE_PATTERN = new RegExp(
  `(${VARIANT_NUMBER_PATTERN})\\s*${VARIANT_UNIT_PATTERN}\\s*(?:x|х|×|\\*)\\s*(${VARIANT_NUMBER_PATTERN})`,
  "i",
);
const VARIANT_MEASURE_PATTERN = new RegExp(
  `(${VARIANT_NUMBER_PATTERN})\\s*${VARIANT_UNIT_PATTERN}`,
  "i",
);
const VARIANT_NUMERIC_PATTERN = new RegExp(`(${VARIANT_NUMBER_PATTERN})`);

const getVariantDisplayValue = (variant) =>
  String(
    variant?.weight ??
      variant?.size ??
      variant?.volume ??
      variant?.taste ??
      variant?.flavor ??
      variant?.name ??
      variant?.productCode ??
      "",
  ).trim();

const parseVariantNumber = (value) =>
  safeNumber(String(value).replace(",", "."), NaN);

const getVariantUnitMeta = (unit) => {
  const normalized = String(unit || "")
    .toLowerCase()
    .replace(".", "")
    .trim();

  if (
    [
      "кілограма",
      "кілограми",
      "кілограмів",
      "кілограм",
      "кг",
      "kg",
    ].includes(normalized)
  ) {
    return { dimension: "mass", factor: 1000 };
  }

  if (
    [
      "міліграма",
      "міліграми",
      "міліграмів",
      "міліграм",
      "мг",
      "mg",
    ].includes(normalized)
  ) {
    return { dimension: "mass", factor: 0.001 };
  }

  if (["грама", "грами", "грамів", "грам", "гр", "г", "g"].includes(normalized)) {
    return { dimension: "mass", factor: 1 };
  }

  if (
    [
      "літра",
      "літри",
      "літрів",
      "літр",
      "л",
      "l",
    ].includes(normalized)
  ) {
    return { dimension: "volume", factor: 1000 };
  }

  if (
    [
      "мілілітра",
      "мілілітри",
      "мілілітрів",
      "мілілітр",
      "мл",
      "ml",
    ].includes(normalized)
  ) {
    return { dimension: "volume", factor: 1 };
  }

  return null;
};

const getVariantSortMeta = (variant) => {
  const rawLabel = getVariantDisplayValue(variant);
  const normalized = rawLabel
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(",", ".")
    .trim();

  if (!normalized) {
    return { type: "text", value: Number.POSITIVE_INFINITY, label: "" };
  }

  const packMeasureMatch = normalized.match(VARIANT_PACK_MEASURE_PATTERN);
  if (packMeasureMatch) {
    const multiplier = parseVariantNumber(packMeasureMatch[1]);
    const amount = parseVariantNumber(packMeasureMatch[2]);
    const unitMeta = getVariantUnitMeta(packMeasureMatch[3]);

    if (Number.isFinite(multiplier) && Number.isFinite(amount) && unitMeta) {
      return {
        type: "measured",
        dimension: unitMeta.dimension,
        value: multiplier * amount * unitMeta.factor,
        label: normalized,
      };
    }
  }

  const reversePackMeasureMatch = normalized.match(
    VARIANT_REVERSE_PACK_MEASURE_PATTERN,
  );
  if (reversePackMeasureMatch) {
    const amount = parseVariantNumber(reversePackMeasureMatch[1]);
    const unitMeta = getVariantUnitMeta(reversePackMeasureMatch[2]);
    const multiplier = parseVariantNumber(reversePackMeasureMatch[3]);

    if (Number.isFinite(multiplier) && Number.isFinite(amount) && unitMeta) {
      return {
        type: "measured",
        dimension: unitMeta.dimension,
        value: multiplier * amount * unitMeta.factor,
        label: normalized,
      };
    }
  }

  const measureMatch = normalized.match(VARIANT_MEASURE_PATTERN);
  if (measureMatch) {
    const amount = parseVariantNumber(measureMatch[1]);
    const unitMeta = getVariantUnitMeta(measureMatch[2]);

    if (Number.isFinite(amount) && unitMeta) {
      return {
        type: "measured",
        dimension: unitMeta.dimension,
        value: amount * unitMeta.factor,
        label: normalized,
      };
    }
  }

  const numberMatch = normalized.match(VARIANT_NUMERIC_PATTERN);
  const numeric = numberMatch ? parseVariantNumber(numberMatch[1]) : NaN;

  if (Number.isFinite(numeric)) {
    return { type: "numeric", value: numeric, label: normalized };
  }

  return { type: "text", value: Number.POSITIVE_INFINITY, label: normalized };
};

const compareVariants = (leftVariant, rightVariant) => {
  const left = getVariantSortMeta(leftVariant);
  const right = getVariantSortMeta(rightVariant);

  const leftOrder = VARIANT_SORT_TYPE_ORDER[left.type] ?? 99;
  const rightOrder = VARIANT_SORT_TYPE_ORDER[right.type] ?? 99;

  if (leftOrder !== rightOrder) return leftOrder - rightOrder;
  if (left.value !== right.value) return left.value - right.value;

  const leftDimensionOrder = VARIANT_DIMENSION_ORDER[left.dimension] ?? 99;
  const rightDimensionOrder = VARIANT_DIMENSION_ORDER[right.dimension] ?? 99;

  if (leftDimensionOrder !== rightDimensionOrder) {
    return leftDimensionOrder - rightDimensionOrder;
  }

  return left.label.localeCompare(right.label, "uk", {
    numeric: true,
    sensitivity: "base",
  });
};

const normalizeVariantIdentifier = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getRequestedVariantParams = (searchParams) => ({
  variantId: normalizeVariantIdentifier(searchParams.get("variantId")),
  productCode: normalizeVariantIdentifier(searchParams.get("productCode")),
  sku: normalizeVariantIdentifier(searchParams.get("sku")),
  article: normalizeVariantIdentifier(searchParams.get("article")),
  weight: normalizeVariantIdentifier(searchParams.get("weight")),
  size: normalizeVariantIdentifier(searchParams.get("size")),
});

const getNormalizedCandidates = (values) =>
  values.map(normalizeVariantIdentifier).filter(Boolean);

const getVariantIdCandidates = (variant) =>
  getNormalizedCandidates([getProductVariantId(variant)]);

const getVariantCodeCandidates = (variant) =>
  getNormalizedCandidates([
    variant?.productCode,
    variant?.sku,
    variant?.article,
  ]);

const getVariantSizeCandidates = (variant) =>
  getNormalizedCandidates([
    variant?.weight,
    variant?.size,
    variant?.volume,
    getVariantDisplayValue(variant),
  ]);

const findVariantByCandidates = (variants, requestedValues, getCandidates) => {
  const requested = requestedValues.filter(Boolean);
  if (!requested.length) return null;

  return (
    variants.find((variant) =>
      getCandidates(variant).some((candidate) => requested.includes(candidate)),
    ) || null
  );
};

const findRequestedVariant = (variantList, requestedParams) => {
  const variants = toArray(variantList);
  const codeMatch = findVariantByCandidates(
    variants,
    [requestedParams.productCode, requestedParams.sku, requestedParams.article],
    getVariantCodeCandidates,
  );

  if (codeMatch) return codeMatch;

  const idMatch = findVariantByCandidates(
    variants,
    [requestedParams.variantId],
    getVariantIdCandidates,
  );

  if (idMatch) return idMatch;

  return findVariantByCandidates(
    variants,
    [requestedParams.weight, requestedParams.size],
    getVariantSizeCandidates,
  );
};

const isSameVariant = (left, right) => {
  if (!left || !right) return false;

  const leftId = normalizeVariantIdentifier(getProductVariantId(left));
  const rightId = normalizeVariantIdentifier(getProductVariantId(right));
  if (leftId && rightId) return leftId === rightId;

  const leftCode = normalizeVariantIdentifier(left.productCode);
  const rightCode = normalizeVariantIdentifier(right.productCode);
  return Boolean(leftCode && rightCode && leftCode === rightCode);
};

const ProductDetail = () => {
  const { revision: promotionRevision } = usePromotions();
  const { slug, code, categorySlug, kindSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useContext(CartContext);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { isInComparison, toggleComparison } = useComparison();
  const { settings: deliverySettings } = useDeliverySettings();

  const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [isProductLoading, setIsProductLoading] = useState(true);
  const [isProductNotFound, setIsProductNotFound] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [relatedProducts, setRelatedProducts] = useState([]);
  const requestedVariantParams = useMemo(
    () => getRequestedVariantParams(searchParams),
    [searchParams],
  );
  const isSelectedVariantOutOfStock = selectedVariant
    ? getProductAvailableQuantity(selectedVariant) <= 0
    : true;
  const hasVisibleMobileStickyBar = Boolean(
    product && selectedVariant && !isSelectedVariantOutOfStock,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchProduct = async () => {
      setIsProductLoading(true);
      setIsProductNotFound(false);

      try {
        const res = await axios.get(
          code
            ? `${backendUrl}/api/products/by-code/${encodeURIComponent(code)}`
            : `${backendUrl}/api/products/slug/${encodeURIComponent(slug)}`,
        );
        if (isCancelled) return;
        const mainProduct = res.data;
        setIsProductNotFound(false);
        setProduct(mainProduct);

        let nextVariant = null;
        let nextVariants = [];

        if (mainProduct.productGroupId) {
          const variantsRes = await axios.get(
            `${backendUrl}/api/products/group/${mainProduct.productGroupId}`,
          );

          nextVariants = toArray(variantsRes.data);
          const availableVariant = nextVariants.find(
            (variant) => (variant.totalAvailable ?? 0) > 0,
          );
          const currentRequestedParams = getRequestedVariantParams(
            new URLSearchParams(window.location.search),
          );
          const requestedVariant = findRequestedVariant(
            nextVariants,
            currentRequestedParams,
          );

          nextVariant =
            requestedVariant ||
            availableVariant ||
            nextVariants.find((variant) => variant._id === mainProduct._id) ||
            nextVariants[0] ||
            mainProduct;
        } else {
          nextVariant = { ...mainProduct };

          try {
            const stockRes = await axios.get(
              `${backendUrl}/api/stock-batches/public`,
              { params: { productCode: nextVariant.productCode } },
            );

            nextVariant.totalAvailable = Number(
              stockRes.data?.totalAvailable || 0,
            );
          } catch {
            nextVariant.totalAvailable = 0;
          }

          nextVariants = [nextVariant];
        }

        if (isCancelled) return;

        setVariants(nextVariants);
        setSelectedVariant(nextVariant);
        setSelectedImageIndex(0);
        setQuantity(1);
        setErrorMessage("");
        setIsProductLoading(false);

        try {
          const relatedLimit = 240;
          const primaryCategory = arrayify(mainProduct.category)[0] || null;
          const primaryRelatedRes = await axios.get(`${backendUrl}/api/products`, {
            params: {
              limit: relatedLimit,
              ...(primaryCategory ? { category: primaryCategory } : {}),
            },
            withCredentials: false,
          });

          let relatedSource = toArray(primaryRelatedRes.data);
          let related = pickRelatedProducts(mainProduct, relatedSource, 4);

          if (related.length < 4 && primaryCategory) {
            const fallbackRelatedRes = await axios.get(
              `${backendUrl}/api/products`,
              {
                params: { limit: relatedLimit },
                withCredentials: false,
              },
            );

            relatedSource = [
              ...relatedSource,
              ...toArray(fallbackRelatedRes.data),
            ];
            related = pickRelatedProducts(mainProduct, relatedSource, 4);
          }

          if (isCancelled) return;

          setRelatedProducts(sortProductsByAvailability(related).slice(0, 4));
        } catch {
          if (isCancelled) return;

          setRelatedProducts([]);
        }
      } catch (err) {
        if (isCancelled) return;

        console.error("Помилка завантаження товару:", err);
        setIsProductNotFound(err?.response?.status === 404);
        setProduct(null);
        setSelectedVariant(null);
        setVariants([]);
        setRelatedProducts([]);
        setIsProductLoading(false);
      }
    };

    fetchProduct();

    return () => {
      isCancelled = true;
    };
  }, [slug, code, backendUrl, promotionRevision]);

  useEffect(() => {
    if (!variants.length) return;

    const requestedVariant = findRequestedVariant(
      variants,
      requestedVariantParams,
    );

    if (!requestedVariant || isSameVariant(selectedVariant, requestedVariant)) {
      return;
    }

    setSelectedVariant(requestedVariant);
    setSelectedImageIndex(0);
    setQuantity(1);
    setErrorMessage("");
  }, [requestedVariantParams, selectedVariant, variants]);

  useEffect(() => {
    if (!product || !selectedVariant || isProductLoading) return;
    if (code ? Number(product.productCode) !== Number(code) : product.slug !== slug && !product.previousSlugs?.includes(slug)) return;

    const nextPath = buildProductDetailPath(product, {
      productCode: selectedVariant?.productCode,
    });
    const currentPath = `${location.pathname}${location.search}`;
    const shouldNormalizePath =
      location.pathname.startsWith("/products/") ||
      location.pathname.startsWith("/product/code/") ||
      searchParams.has("variantId") || product.previousSlugs?.includes(slug);

    if (!shouldNormalizePath || !nextPath || nextPath === currentPath) return;

    navigate(nextPath, {
      replace: true,
      state: { ...(location.state || {}), preserveScrollPosition: true },
    });
  }, [
    code,
    slug,
    isProductLoading,
    location.pathname,
    location.search,
    location.state,
    navigate,
    product,
    searchParams,
    selectedVariant,
  ]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--mobile-sticky-bar-offset",
      hasVisibleMobileStickyBar
        ? "calc(var(--mobile-sticky-bar-height, 84px) + env(safe-area-inset-bottom) + 16px)"
        : "0px",
    );

    return () => {
      root.style.setProperty("--mobile-sticky-bar-offset", "0px");
    };
  }, [hasVisibleMobileStickyBar]);

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    setSelectedImageIndex(0);
    setQuantity(1);
    setErrorMessage("");

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("variantId");
    if (variant.productCode) {
      nextParams.set("productCode", variant.productCode);
    } else {
      nextParams.delete("productCode");
    }
    nextParams.delete("sku");
    nextParams.delete("article");
    nextParams.delete("weight");
    nextParams.delete("size");
    setSearchParams(nextParams, {
      replace: true,
      state: { preserveScrollPosition: true },
    });
  };

  const handleQuantityChange = (change) => {
    const max = getProductAvailableQuantity(selectedVariant);
    const newQty = quantity + change;

    if (newQty < 1) return;

    if (newQty > max) {
      setErrorMessage(
        `Неможливо додати більше. На залишку лише ${max} од. товару`,
      );
      return;
    }

    setQuantity(newQty);
    setErrorMessage("");
  };

  const buildCartItem = (item, baseProduct = product) => {
    const images = buildImageList(baseProduct, item).filter(
      (image) => image !== DEFAULT_IMAGE,
    );

    return {
      ...item,
      productId: item?._id || baseProduct?._id,
      slug: baseProduct?.slug || item?.slug || "",
      name: baseProduct?.name || item?.name || "",
      manufacturer: baseProduct?.manufacturer || item?.manufacturer || "",
      countryOfOrigin:
        baseProduct?.countryOfOrigin || item?.countryOfOrigin || "",
      weight: item?.weight || baseProduct?.weight || "",
      image:
        getProductImage(item, "thumbnail", 0, "") ||
        getProductImage(baseProduct, "thumbnail", 0, "") ||
        images[0] ||
        DEFAULT_IMAGE,
      images: images.length ? images : [DEFAULT_IMAGE],
      totalAvailable:
        item?.totalAvailable ?? item?.totalQuantity ?? item?.maxAvailable ?? 0,
    };
  };

  const handleAddToCart = () => {
    if (!selectedVariant) return;
    if (getProductAvailableQuantity(selectedVariant) <= 0) return;

    addToCart(buildCartItem(selectedVariant), quantity);
  };

  const handleRelatedAddToCart = (item) => {
    const available = item?.totalAvailable ?? item?.totalQuantity ?? 0;
    if (available <= 0) return;

    addToCart(buildCartItem(item, item), 1);
  };

  const sortedVariants = useMemo(
    () => [...variants].sort(compareVariants),
    [variants],
  );

  const isStaleProduct = Boolean(
    product && (code
      ? Number(product.productCode) !== Number(code)
      : product.slug && product.slug !== slug && !product.previousSlugs?.includes(slug)),
  );

  if (isProductLoading || isStaleProduct) {
    return (
      <div className={styles.productPageWrapper}>
        <div
          className={productsStyles.loadingState}
          role="status"
          aria-live="polite"
        >
          <span
            className={productsStyles.loadingSpinner}
            aria-hidden="true"
          />
          <p className={productsStyles.loadingText}>Завантажується...</p>
        </div>
      </div>
    );
  }

  if (isProductNotFound) {
    return (
      <div className={styles.productPageWrapper}>
        <Helmet>
          <title>{formatTitle("Товар не знайдено")}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className={styles.loadingCard} role="status">
          <div>
            <h1>Товар не знайдено</h1>
            <p>Можливо, картку товару тимчасово приховано або посилання застаріло.</p>
            <Link to="/products">Повернутися до каталогу</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!product || !selectedVariant) {
    return (
      <div className={styles.productPageWrapper}>
        <div className={styles.loadingCard}>Завантаження товару...</div>
      </div>
    );
  }

  const imagePairs = buildImagePairs(product, selectedVariant);
  const images = imagePairs.map(
    (image) => image.full || image.thumbnail || DEFAULT_IMAGE,
  );
  const additionalImages = imagePairs.slice(1);
  const isOutOfStock = isSelectedVariantOutOfStock;
  const ratingRaw =
    selectedVariant?.averageRating ??
    selectedVariant?.avgRating ??
    selectedVariant?.rating ??
    product?.averageRating ??
    product?.avgRating ??
    product?.rating ??
    0;
  const ratingValue = Math.min(
    5,
    Math.max(0, Math.round(safeNumber(ratingRaw) * 10) / 10),
  );
  const reviewsRaw =
    selectedVariant?.reviewCount ??
    selectedVariant?.reviewsCount ??
    selectedVariant?.totalReviews ??
    product?.reviewCount ??
    product?.reviewsCount ??
    product?.totalReviews ??
    (Array.isArray(product?.reviews) ? product.reviews.length : 0);
  const reviewsCount = Math.max(0, Math.round(safeNumber(reviewsRaw)));
  const hasRatingReviews = ratingValue > 0 && reviewsCount > 0;
  const selectedQuantity = Math.max(1, Math.round(safeNumber(quantity, 1)));
  const regularPrice = getProductRegularPrice(selectedVariant);
  const currentPrice = getActualProductPrice(selectedVariant);
  const productSaving = getProductSaving(selectedVariant);
  const rawDiscountPercent = Math.round(getCardDiscountPercent(selectedVariant));
  const hasValidSalePricePair =
    Number.isFinite(regularPrice) &&
    Number.isFinite(currentPrice) &&
    regularPrice > 0 &&
    currentPrice > 0 &&
    regularPrice > currentPrice;
  const hasActiveDiscount =
    !isOutOfStock &&
    hasValidSalePricePair &&
    productSaving > 0 &&
    rawDiscountPercent > 0 &&
    regularPrice - currentPrice > 0;
  const discountPercent = hasActiveDiscount ? rawDiscountPercent : 0;
  const baseDisplayPrice = regularPrice > 0 ? regularPrice : currentPrice;
  const displayPrice = hasActiveDiscount ? currentPrice : baseDisplayPrice;
  const buttonTotalPrice = safeNumber(displayPrice) * selectedQuantity;
  const deliveryTotalPrice = buttonTotalPrice;
  const { branchFreeDeliveryThreshold, courierFreeDeliveryThreshold } =
    deliverySettings.novaPoshta;
  const deliveryConditions = [
    {
      label: 'У відділення "Нової пошти" або у поштомат',
      isFree: deliveryTotalPrice >= branchFreeDeliveryThreshold,
      paidText: "за тарифом перевізника",
      hint: `(або безкоштовно для замовлень від ${formatUah(branchFreeDeliveryThreshold)})`,
    },
    {
      label: 'Кур\'єром "Нової пошти"',
      isFree: deliveryTotalPrice >= courierFreeDeliveryThreshold,
      paidText: "за тарифом перевізника",
      hint: `(або безкоштовно для замовлень від ${formatUah(courierFreeDeliveryThreshold)})`,
    },
  ];
  const currentPriceClassName = `${styles.currentPrice} ${
    isOutOfStock
      ? styles.unavailablePrice
      : hasActiveDiscount
        ? styles.discountPrice
        : styles.regularPrice
  }`.trim();
  const savingTextClassName = `${styles.savingText} ${
    isOutOfStock ? styles.unavailablePrice : ""
  }`.trim();
  const mobileStickyPriceClassName = `${styles.stickyPrice} ${
    hasActiveDiscount ? styles.stickyDiscountPrice : ""
  }`.trim();
  const addToCartButtonLabel = `Додати в кошик - ${formatPrice(
    buttonTotalPrice,
  )} грн`;
  const unitPriceInfo = calculatePricePerKgOrLitre(selectedVariant);
  const descriptionBlocks = parseDescriptionBlocks(product.description);
  const primaryDescription =
    product.description ||
    "Детальний опис товару скоро буде оновлено. Якщо потрібна консультація, наші менеджери допоможуть підібрати найкращий варіант.";
  const unitPriceChip = unitPriceInfo
    ? `${unitPriceInfo.label}: ${unitPriceInfo.value} ₴`
    : null;
  const details = [
    { title: "Підходить для", value: normalizeProductCategories(selectedVariant?.category ?? product.category) },
    { title: "Вид", value: product.kind },
    {
      title: "Клас корму",
      value: selectedVariant?.feedClass || product.feedClass,
    },
    { title: "Тип", value: product.type },
    {
      title: "Лікувальний корм",
      value: getYesNoDetailValue(selectedVariant?.therapeuticFeed),
    },
    {
      title: "Гіпоалергенний",
      value: getYesNoDetailValue(product.hypoallergenic),
    },
    { title: "Призначення", value: arrayify(product.purpose) },
    {
      title: "Вік тварини",
      value:
        arrayify(selectedVariant?.animalAge).length
          ? arrayify(selectedVariant.animalAge)
          : arrayify(product.animalAge),
    },
    {
      title: "Порода",
      value: selectedVariant?.breed?.length ? selectedVariant.breed : product.breed,
    },
    {
      title: "Основні інгредієнти",
      value: selectedVariant?.mainIngredients?.length
        ? selectedVariant.mainIngredients
        : product.mainIngredients,
    },
    { title: "Виробник", value: product.manufacturer },
    { title: "Країна-виробник", value: product.countryOfOrigin },
    { title: "Розмір упаковки", value: selectedVariant?.weight },
    { title: "Код товару", value: selectedVariant?.productCode },
  ].filter((detail) => {
    if (Array.isArray(detail.value)) {
      return detail.value.some((value) => String(value || "").trim());
    }
    return detail.value && detail.value !== "Н/Д";
  });
  const detailsWithoutProductCode = details.filter(
    (detail) => detail.title !== "Код товару"
  );
  const selectedKey = selectedVariant.productCode || selectedVariant._id;

  const variantWeight = selectedVariant?.weight || product.weight || "";
  const productPath = buildProductDetailPath(product, {
    productCode: selectedVariant?.productCode,
  });
  const pageUrl = absoluteUrl(productPath);
  const pageImage = images[0] || DEFAULT_IMAGE;
  const pageImageUrl = absoluteUrl(pageImage);
  const productTitleText = getProductDisplayName(product, selectedVariant || { weight: variantWeight });
  const pageTitle = formatTitle(`${productTitleText} - купити`);
  const pageDescription = `Купуйте ${productTitleText}${
    product.manufacturer ? ` від ${product.manufacturer}` : ""
  } у MyZooShop. Актуальна ціна, наявність і доставка по Україні.`;
  const productSchema = buildProductSchema({
    product,
    variant: selectedVariant,
    url: pageUrl,
    image: pageImageUrl,
  });
  const productCategoryItems = getProductCategoryItems(product);
  const primaryCategory = getPrimaryCategoryValue(product);
  const primaryCategoryItem =
    getCategoryByQuery(primaryCategory) || productCategoryItems[0] || null;
  const productKindValues = arrayify(product?.kind);
  const primaryKind = productKindValues[0] || null;
  const navigationState = location.state || {};
  const isPromotionBreadcrumbContext = Boolean(navigationState.fromPromotions);
  const stateCategoryItem = navigationState.animalSlug
    ? getCategoryBySlug(navigationState.animalSlug)
    : null;
  const routeCategoryItem = categorySlug ? getCategoryBySlug(categorySlug) : null;
  const stateContextCategoryItem =
    stateCategoryItem && productHasCategoryItem(product, stateCategoryItem)
      ? stateCategoryItem
      : null;
  const routeContextCategoryItem =
    !isPromotionBreadcrumbContext &&
    !navigationState.fromBrand &&
    routeCategoryItem &&
    productHasCategoryItem(product, routeCategoryItem)
      ? routeCategoryItem
      : null;
  const contextCategoryItem =
    stateContextCategoryItem || routeContextCategoryItem;
  const shouldSuppressDefaultCategory =
    Boolean(navigationState.fromBrand) && !stateContextCategoryItem;
  const categoryBreadcrumbItem = isPromotionBreadcrumbContext
    ? contextCategoryItem
    : contextCategoryItem ||
      (shouldSuppressDefaultCategory ? null : primaryCategoryItem);
  const stateKindBreadcrumbValue =
    navigationState.kind ||
    (navigationState.kindSlug
      ? findKindBySlug(productKindValues, navigationState.kindSlug)
      : null);
  const routeKindBreadcrumbValue = kindSlug
    ? findKindBySlug(productKindValues, kindSlug)
    : null;
  const rawKindBreadcrumbValue = categoryBreadcrumbItem
    ? stateKindBreadcrumbValue || routeKindBreadcrumbValue || primaryKind
    : null;
  const kindBreadcrumbLabel = formatKindBreadcrumbLabel(
    rawKindBreadcrumbValue,
    categoryBreadcrumbItem,
  );
  const categoryBreadcrumbPath = categoryBreadcrumbItem
    ? isPromotionBreadcrumbContext
      ? buildPromotionCategoryPath(categoryBreadcrumbItem.slug)
      : categoryBreadcrumbItem.path
    : null;
  const kindBreadcrumbPath =
    categoryBreadcrumbItem?.slug && kindBreadcrumbLabel
      ? isPromotionBreadcrumbContext
        ? buildPromotionCategoryKindPath(
            categoryBreadcrumbItem.slug,
            rawKindBreadcrumbValue,
          )
        : buildCategoryKindPath(categoryBreadcrumbItem.slug, rawKindBreadcrumbValue)
      : null;

  const breadcrumbs = [
    { label: "Головна", to: "/" },
    ...(isPromotionBreadcrumbContext
      ? [{ label: "Акційні товари", to: PROMOTIONS_PATH }]
      : categoryBreadcrumbItem
      ? []
      : [{ label: "Каталог товарів", to: "/products" }]),
    ...(categoryBreadcrumbItem && categoryBreadcrumbPath
      ? [
          {
            label: formatCategoryBreadcrumbLabel(categoryBreadcrumbItem),
            to: categoryBreadcrumbPath,
          },
        ]
      : []),
    ...(kindBreadcrumbLabel && kindBreadcrumbPath
      ? [
          {
            label: kindBreadcrumbLabel,
            to: kindBreadcrumbPath,
          },
        ]
      : []),
    ...(product?.manufacturer
      ? [
          {
            label: product.manufacturer,
            to: buildBrandPath(product.manufacturer),
          },
        ]
      : []),
    { label: productTitleText, to: "#" },
  ];
  const buildRelatedProductLinkState = (item) => {
    const relatedKind = arrayify(item?.kind)[0] || rawKindBreadcrumbValue;

    if (isPromotionBreadcrumbContext) {
      return {
        fromPromotions: true,
        ...(categoryBreadcrumbItem?.slug
          ? { animalSlug: categoryBreadcrumbItem.slug }
          : {}),
        ...(relatedKind ? { kind: relatedKind } : {}),
        sourcePath: location.pathname,
      };
    }

    if (categoryBreadcrumbItem?.slug) {
      return {
        fromCategory: true,
        fromPromotions: false,
        animalSlug: categoryBreadcrumbItem.slug,
        ...(relatedKind ? { kind: relatedKind } : {}),
        sourcePath: location.pathname,
      };
    }

    if (navigationState.fromBrand) {
      return {
        fromBrand: true,
        brandSlug: navigationState.brandSlug || null,
        sourcePath: location.pathname,
      };
    }

    return undefined;
  };
  const tabs = [
    { id: "description", label: "Опис" },
    { id: "specifications", label: "Характеристики" },
    { id: "reviews", label: "Відгук" },
  ];

  const renderDescription = () => (
    <>
      {descriptionBlocks.length > 0 ? (
        descriptionBlocks.map((block, index) => {
          const shouldRenderTitle = shouldRenderDescriptionBlockTitle(block.title);
          const isBulletBlock = [
            "переваги",
            "показання",
            "протипоказання",
            "рекомендації щодо застосування",
            "рекомендації щодо застосування препарату",
          ].includes(block.title.toLowerCase());

          if (isBulletBlock) {
            return (
              <div key={`${block.title}-${index}`} className={styles.descriptionBlock}>
                <strong>{block.title}:</strong>
                <ul className={styles.bulletList}>
                  {block.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            );
          }

          return (
            <p key={`${block.title}-${index}`} className={styles.descriptionBlock}>
              {shouldRenderTitle && block.content !== block.title && (
                <strong>{block.title}:</strong>
              )}
              {block.content}
            </p>
          );
        })
      ) : (
        <p>{primaryDescription}</p>
      )}

    </>
  );

  const renderRatingSummary = () => {
    if (!hasRatingReviews) return null;

    return (
      <div className={styles.ratingRow}>
        <span
          className={styles.ratingStars}
          aria-label={`Рейтинг ${ratingValue.toFixed(1)} з 5`}
        >
          <span className={styles.ratingStarsBase} aria-hidden="true">
            {STAR_SYMBOLS}
          </span>
          <span
            className={styles.ratingStarsFill}
            style={{ width: `${(ratingValue / 5) * 100}%` }}
            aria-hidden="true"
          >
            {STAR_SYMBOLS}
          </span>
        </span>
        <span>{`${ratingValue.toFixed(1)} (${reviewsCount} ${pluralizeReviews(
          reviewsCount,
        )})`}</span>
      </div>
    );
  };

  const renderTabContent = () => {
    if (activeTab === "specifications") {
      return (
        <div className={styles.specGrid}>
          {detailsWithoutProductCode.map((detail) => (
            <div key={detail.title} className={styles.specRow}>
              <span>{detail.title}</span>
              <strong>{formatDetailValue(detail.value)}</strong>
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === "reviews") {
      return (
        <>
          <ReviewForm productCode={product.productCode} />
        </>
      );
    }

    return renderDescription();
  };

  return (
    <div
      className={`${styles.productPageWrapper} ${
        hasVisibleMobileStickyBar ? styles.hasMobileStickyBar : ""
      }`.trim()}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:locale" content={SITE_LOCALE} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImageUrl} />
        <meta
          property="product:price:amount"
          content={String(displayPrice || "")}
        />
        <meta property="product:price:currency" content="UAH" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>

      <Breadcrumbs paths={breadcrumbs} mobileSingleLine />

      <main className={styles.productDetailMain}>
        <section className={styles.productMediaColumn}>
          <div className={styles.galleryCard}>
            <div className={styles.galleryWrapper}>
              <div className={styles.mainImageShell}>
                <img
                  src={imagePairs[selectedImageIndex]?.full || DEFAULT_IMAGE}
                  alt={`${product.name} - зображення`}
                  className={styles.productMainImage}
                  decoding="async"
                  onClick={() => setIsImageOpen(true)}
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_IMAGE;
                  }}
                />

                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      className={`${styles.galleryNavButton} ${styles.prevButton}`}
                      onClick={() =>
                        setSelectedImageIndex(
                          (prev) => (prev - 1 + images.length) % images.length,
                        )
                      }
                      aria-label="Попереднє зображення"
                    >
                      <FiChevronLeft aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={`${styles.galleryNavButton} ${styles.nextButton}`}
                      onClick={() =>
                        setSelectedImageIndex((prev) => (prev + 1) % images.length)
                      }
                      aria-label="Наступне зображення"
                    >
                      <FiChevronRight aria-hidden="true" />
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className={styles.zoomButton}
                  onClick={() => setIsImageOpen(true)}
                  aria-label="Збільшити фото"
                >
                  <FiMaximize2 aria-hidden="true" />
                </button>
              </div>

              {additionalImages.length > 0 && (
                <div className={styles.thumbnailList} aria-label="Мініатюри товару">
                  {additionalImages.map((image, index) => {
                    const fullImageIndex = index + 1;

                    return (
                      <button
                        key={`${image.full || image.thumbnail}-${fullImageIndex}`}
                        type="button"
                        className={`${styles.thumbnailButton} ${
                          selectedImageIndex === fullImageIndex
                            ? styles.activeThumbnail
                            : ""
                        }`}
                        onClick={() => setSelectedImageIndex(fullImageIndex)}
                        aria-label={`Показати додаткове зображення ${index + 1}`}
                      >
                        <img
                          src={image.thumbnail || image.full || DEFAULT_IMAGE}
                          alt={`${product.name} - додаткове зображення ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          onError={(event) => {
                            event.currentTarget.src = DEFAULT_IMAGE;
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <section className={styles.productTabsCard}>
            <div className={styles.tabsHeader} role="tablist" aria-label="Інформація про товар">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`${styles.tabButton} ${
                    activeTab === tab.id ? styles.activeTab : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className={styles.tabContent}>{renderTabContent()}</div>
          </section>

          {relatedProducts.length > 0 && (
            <section className={styles.relatedProductsSection}>
              <div className={styles.relatedProductsHeader}>
                <h2>Схожі товари</h2>
                <Link to="/products">Переглянути всі</Link>
              </div>

              <div className={styles.relatedProductsGrid}>
                {relatedProducts.map((item) => {
                  const relatedImage = getProductImage(item, "thumbnail", 0, DEFAULT_IMAGE);
                  const relatedRegularPrice = getProductRegularPrice(item);
                  const relatedDiscountPrice = getProductDiscountPrice(item);
                  const isRelatedOutOfStock =
                    getProductAvailableQuantity(item) <= 0;
                  const rawRelatedDiscount = Math.round(
                    getCardDiscountPercent(item),
                  );
                  const hasRelatedDiscount =
                    !isRelatedOutOfStock &&
                    rawRelatedDiscount > 0 &&
                    relatedRegularPrice > 0 &&
                    relatedDiscountPrice > 0 &&
                    relatedDiscountPrice < relatedRegularPrice;
                  const relatedDiscount = hasRelatedDiscount
                    ? rawRelatedDiscount
                    : 0;
                  const relatedPrice = hasRelatedDiscount
                    ? relatedDiscountPrice
                    : relatedRegularPrice;
                  const relatedTitle = getProductDisplayName(item);

                  return (
                    <article
                      key={item._id || item.productCode}
                      className={styles.relatedProductCard}
                    >
                      <Link
                        to={buildProductDetailPath(item)}
                        state={buildRelatedProductLinkState(item)}
                        className={styles.relatedProductLink}
                        aria-label={relatedTitle}
                      >
                        <div className={styles.relatedProductImageWrap}>
                          <img
                            src={relatedImage}
                            alt={item.name}
                            className={`${styles.relatedProductImage} ${
                              isRelatedOutOfStock
                                ? styles.relatedMutedProductImage
                                : ""
                            }`}
                            loading="lazy"
                            decoding="async"
                            onError={(event) => {
                              event.currentTarget.src = DEFAULT_IMAGE;
                            }}
                          />

                          {hasRelatedDiscount && (
                            <span
                              className={styles.relatedDiscountPercentBadge}
                              aria-label={`Знижка -${relatedDiscount}%`}
                            >
                              <RiDiscountPercentFill
                                className={styles.relatedDiscountIcon}
                                aria-hidden="true"
                              />
                              <span className={styles.relatedDiscountPercentText}>
                                -{relatedDiscount}%
                              </span>
                            </span>
                          )}
                        </div>

                        <h4 className={styles.relatedProductTitle}>
                          {relatedTitle}
                        </h4>
                      </Link>

                      <ProductRatingSummary product={item} compact />

                      <div className={styles.relatedCardBottom}>
                        {isRelatedOutOfStock ? (
                          <div
                            className={`${styles.relatedPriceWrapper} ${styles.relatedOutOfStockState}`}
                          >
                            {relatedRegularPrice > 0 && (
                              <span className={styles.relatedDisabledPrice}>
                                {formatPrice(relatedRegularPrice)} ₴
                              </span>
                            )}
                            Немає в наявності
                          </div>
                        ) : (
                          <>
                            <div className={styles.relatedPriceWrapper}>
                              {hasRelatedDiscount &&
                                relatedRegularPrice > relatedDiscountPrice && (
                                <span className={styles.relatedOldPrice}>
                                  {formatPrice(relatedRegularPrice)} ₴
                                </span>
                              )}
                              <span
                                className={
                                  hasRelatedDiscount
                                    ? styles.relatedDiscountPrice
                                    : styles.relatedProductPrice
                                }
                              >
                                {formatPrice(relatedPrice)} ₴
                              </span>
                            </div>

                            <button
                              type="button"
                              className={styles.relatedAddToCartButton}
                              onClick={() => handleRelatedAddToCart(item)}
                              aria-label="Додати схожий товар у кошик"
                            >
                              <BsBasket aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </section>

        <aside className={styles.productSummaryColumn}>
          <section className={styles.productInfoCard}>
            <h1 className={styles.productTitle}>
              {productTitleText}
            </h1>

            <p className={styles.productCode}>
              Код товару (артикул): {selectedVariant?.productCode || "Н/Д"}
            </p>

            {renderRatingSummary()}

            <div className={styles.badgeRow}>
              <span className={`${styles.badge} ${styles.badgeOrange}`}>
                Хіт продажу
              </span>
              {product.feedClass && (
                <span className={`${styles.badge} ${styles.badgeGreen}`}>
                  {product.feedClass}
                </span>
              )}
              {hasActiveDiscount && (
                <span className={`${styles.badge} ${styles.badgeRed}`}>
                  Знижка -{discountPercent}%
                </span>
              )}
            </div>

            <div className={styles.priceAvailabilityBox}>
              <div>
                <div className={styles.priceLine}>
                  <span className={currentPriceClassName}>
                    {formatPrice(displayPrice)} ₴
                  </span>
                  {hasActiveDiscount && (
                    <span className={styles.oldPrice}>
                      {formatPrice(regularPrice)} ₴
                    </span>
                  )}
                </div>
                {hasActiveDiscount && productSaving > 0 && (
                  <p className={savingTextClassName}>
                    Ви заощаджуєте {formatPrice(productSaving)} ₴
                  </p>
                )}
              </div>

              <div className={styles.availabilityActions}>
                <span
                  className={`${styles.availability} ${
                    isOutOfStock ? styles.unavailable : ""
                  }`}
                >
                  <FiCheckCircle aria-hidden="true" />
                  {isOutOfStock ? "Немає в наявності" : "В наявності"}
                </span>
                <button
                  type="button"
                  className={`${styles.favoriteButton} ${styles.statusFavoriteButton} ${
                    isInWishlist(selectedVariant._id) ? styles.activeFavorite : ""
                  }`}
                  onClick={() =>
                    isInWishlist(selectedVariant._id)
                      ? removeFromWishlist(selectedVariant._id)
                      : addToWishlist({ ...product, ...selectedVariant })
                  }
                  aria-label={
                    isInWishlist(selectedVariant._id)
                      ? "Видалити з обраного"
                      : "Додати в обране"
                  }
                >
                  {isInWishlist(selectedVariant._id) ? (
                    <FaHeart aria-hidden="true" />
                  ) : (
                    <FiHeart aria-hidden="true" />
                  )}
                </button>
                  <button
                    type="button"
                    className={`${styles.favoriteButton} ${styles.statusFavoriteButton} ${
                      isInComparison(selectedVariant._id)
                        ? styles.activeComparison
                        : ""
                    }`}
                    onClick={() =>
                      toggleComparison({ ...product, ...selectedVariant })
                    }
                    aria-label={
                      isInComparison(selectedVariant._id)
                        ? "Видалити з порівняння"
                        : "Додати до порівняння"
                    }
                    aria-pressed={isInComparison(selectedVariant._id)}
                    title={
                      isInComparison(selectedVariant._id)
                        ? "Видалити з порівняння"
                        : "Додати до порівняння"
                    }
                  >
                    <PiScales aria-hidden="true" />
                  </button>
              </div>
            </div>

            {unitPriceChip && (
              <div className={styles.benefitChips}>
                <span className={styles.benefitChip}>
                  <FiCheckCircle aria-hidden="true" />
                  {unitPriceChip}
                </span>
              </div>
            )}

            {product.productGroupId && variants.length > 0 && (
              <div className={styles.optionGroup}>
                <label>Оберіть розмір продукту</label>
                <div className={styles.optionButtons}>
                  {sortedVariants.map((variant) => {
                    const key = variant.productCode || variant._id;
                    const displayValue = getVariantDisplayValue(variant);
                    const isVariantOutOfStock =
                      safeNumber(
                        variant.totalAvailable ??
                          variant.totalQuantity ??
                          variant.maxAvailable,
                      ) <= 0;

                    return (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.optionButton} ${
                          selectedKey === key ? styles.selectedOption : ""
                        } ${
                          isVariantOutOfStock ? styles.outOfStockOption : ""
                        }`}
                        onClick={() => handleVariantChange(variant)}
                      >
                        {displayValue || "Н/Д"}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {errorMessage && (
              <p className={styles.errorMessage}>{errorMessage}</p>
            )}

            {!isOutOfStock && (
              <div className={styles.purchaseRow}>
                <div className={styles.quantityControl}>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    aria-label="Зменшити кількість"
                  >
                    -
                  </button>
                  <span>{quantity}</span>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= getProductAvailableQuantity(selectedVariant)}
                    aria-label="Збільшити кількість"
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  className={styles.addToCartButton}
                  onClick={handleAddToCart}
                >
                  <FiShoppingCart aria-hidden="true" />
                  {addToCartButtonLabel}
                </button>
              </div>
            )}

          </section>

          <section className={styles.serviceCard}>
            <div className={styles.serviceItem}>
              <img
                src={NOVA_POSHTA_LOGO}
                alt="Нова пошта"
                className={`${styles.serviceIcon} ${styles.novaPoshtaLogo}`}
              />
              <div className={styles.serviceItemContent}>
                <strong>Доставка Новою поштою</strong>
                <span className={styles.serviceLine}>
                  Самовивіз з відділення, поштомату або кур&apos;єром
                </span>
                <span className={styles.deliveryConditionsTitle}>
                  Доставимо цей товар:
                </span>
                <div
                  className={styles.deliveryConditions}
                  aria-label="Умови доставки для поточної суми"
                >
                  {deliveryConditions.map((condition) => (
                    <div
                      className={styles.deliveryConditionItem}
                      key={condition.label}
                    >
                      <div className={styles.deliveryConditionRow}>
                        <span className={styles.deliveryConditionLabel}>
                          {condition.label}
                        </span>
                        <strong
                          className={`${styles.deliveryConditionValue} ${
                            condition.isFree
                              ? styles.freeDelivery
                              : styles.paidDelivery
                          }`}
                        >
                          {condition.isFree
                            ? "безкоштовно"
                            : condition.paidText}
                        </strong>
                      </div>
                      {!condition.isFree && (
                        <div className={styles.deliveryConditionHint}>
                          {condition.hint}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.serviceItem}>
              <MdPayment
                className={`${styles.serviceIcon} ${styles.paymentServiceIcon}`}
                aria-hidden="true"
              />
              <div className={styles.serviceItemContent}>
                <strong>Спосіб оплати</strong>
                <span className={styles.serviceLine}>
                  Карткою он-лайн на сайті, у відділенні "Нової пошти" при отриманні та за
                  реквізитами.
                </span>
              </div>
            </div>

            <div className={styles.serviceItem}>
              <FiShield className={styles.serviceIcon} aria-hidden="true" />
              <div className={styles.serviceItemContent}>
                <strong>Оригінальна продукція</strong>
                <span className={styles.serviceLine}>
                  Працюємо з офіційними постачальниками
                </span>
              </div>
            </div>
          </section>
        </aside>
      </main>

      {isImageOpen && (
        <ProductImageLightbox
          images={imagePairs}
          activeIndex={selectedImageIndex}
          onActiveIndexChange={setSelectedImageIndex}
          onClose={() => setIsImageOpen(false)}
          productName={product.name}
        />
      )}

      {!isOutOfStock && (
        <div className={styles.mobileStickyBar}>
          <div className={styles.mobileStickySummary}>
            <div className={mobileStickyPriceClassName}>
              {formatPrice(displayPrice)} ₴
            </div>
            {hasActiveDiscount && (
              <div className={styles.stickyOldPrice}>
                {formatPrice(regularPrice)} ₴
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddToCart}
          >
            <FiShoppingCart aria-hidden="true" />
            Додати в кошик
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
